import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError } from '@/shared/api/client';
import { integrations, type CompanyLookup } from '@/shared/api/resources';
import {
  cnpjCompleto,
  planoDoPreenchimento,
  type CampoDaReceita,
} from '@/shared/lib/consultaCnpj';

/**
 * Consulta de CNPJ que preenche um formulario de cadastro (ADR 0092).
 *
 * O formulario diz o que cada campo tem e como aplicar o que a Receita trouxe; a
 * consulta so preenche campo vazio e guarda os campos com valor diferente para a
 * pessoa decidir (`substituir` ou `manter`). A falha nao trava nada: o formulario
 * continua livre para digitar.
 */
export interface PreenchimentoPelaReceita<C extends string> {
  /** Os campos com o valor atual e o da Receita. Chamado na volta da consulta. */
  planejar: (resultado: CompanyLookup) => CampoDaReceita<C>[];
  /** Aplica os valores da Receita nos campos escolhidos. */
  aplicar: (chaves: C[], resultado: CompanyLookup) => void;
}

export type SituacaoDaConsultaCnpj<C extends string> =
  | { tipo: 'ocioso' }
  | { tipo: 'consultando'; cnpj: string }
  | { tipo: 'erro'; cnpj: string; mensagem: string; desligada: boolean }
  | {
      tipo: 'pronta';
      cnpj: string;
      resultado: CompanyLookup;
      preenchidos: CampoDaReceita<C>[];
      divergentes: CampoDaReceita<C>[];
    };

export interface ConsultaCnpj<C extends string> {
  situacao: SituacaoDaConsultaCnpj<C>;
  /**
   * Acompanha o documento digitado: consulta quando ele vira um CNPJ valido e
   * diferente do ultimo, e esquece a consulta quando o CNPJ muda.
   */
  acompanhar: (documento: string) => void;
  /** Consulta de novo, mesmo que seja o ultimo CNPJ (o botao Buscar na Receita). */
  consultar: (documento: string) => void;
  substituir: () => void;
  manter: () => void;
}

export function useConsultaCnpj<C extends string>(
  preenchimento: PreenchimentoPelaReceita<C>,
): ConsultaCnpj<C> {
  const [situacao, setSituacao] = useState<SituacaoDaConsultaCnpj<C>>({
    tipo: 'ocioso',
  });
  // O ultimo CNPJ pedido: a resposta de uma consulta que outra passou na frente e
  // descartada, e o mesmo CNPJ nao e consultado duas vezes seguidas.
  const ultimo = useRef<string | null>(null);
  // O preenchimento mais recente, para que a volta da consulta leia o formulario como
  // esta agora, e nao como estava quando a consulta saiu.
  const atual = useRef(preenchimento);
  useEffect(() => {
    atual.current = preenchimento;
  });

  const executar = useCallback(async (cnpj: string) => {
    ultimo.current = cnpj;
    setSituacao({ tipo: 'consultando', cnpj });
    try {
      const resultado = await integrations.lookupCompany(cnpj);
      if (ultimo.current !== cnpj) return;
      const { preencher, divergentes } = planoDoPreenchimento(
        atual.current.planejar(resultado),
      );
      atual.current.aplicar(
        preencher.map((campo) => campo.chave),
        resultado,
      );
      setSituacao({
        tipo: 'pronta',
        cnpj,
        resultado,
        preenchidos: preencher,
        divergentes,
      });
    } catch (erro) {
      if (ultimo.current !== cnpj) return;
      setSituacao({
        tipo: 'erro',
        cnpj,
        mensagem:
          erro instanceof Error && erro.message
            ? erro.message
            : 'A consulta de CNPJ nao respondeu.',
        desligada:
          erro instanceof ApiError && erro.code === 'INTEGRACOES_SEM_ADAPTADOR',
      });
    }
  }, []);

  const acompanhar = useCallback(
    (documento: string) => {
      const cnpj = cnpjCompleto(documento);
      if (cnpj) {
        if (cnpj !== ultimo.current) void executar(cnpj);
        return;
      }
      // O CNPJ deixou de ser o consultado: o aviso falaria de outra empresa.
      if (ultimo.current !== null) {
        ultimo.current = null;
        setSituacao({ tipo: 'ocioso' });
      }
    },
    [executar],
  );

  const consultar = useCallback(
    (documento: string) => {
      const cnpj = cnpjCompleto(documento);
      if (cnpj) void executar(cnpj);
    },
    [executar],
  );

  const substituir = useCallback(() => {
    if (situacao.tipo !== 'pronta' || situacao.divergentes.length === 0) return;
    atual.current.aplicar(
      situacao.divergentes.map((campo) => campo.chave),
      situacao.resultado,
    );
    setSituacao({
      ...situacao,
      preenchidos: [...situacao.preenchidos, ...situacao.divergentes],
      divergentes: [],
    });
  }, [situacao]);

  const manter = useCallback(() => {
    setSituacao((anterior) =>
      anterior.tipo === 'pronta' ? { ...anterior, divergentes: [] } : anterior,
    );
  }, []);

  return { situacao, acompanhar, consultar, substituir, manter };
}
